<template>
    <VeeForm class="form user-form" @submit="onSubmit">
        <MDBRow>
            <VehicleTypeSelector v-if="showTyreAndPartnerFields" :selections="selections" :loading="loading"
                @changeSelection="selectValue" />

            <BusinessPartnerSelector v-if="showTyreAndPartnerFields" :bussinessPartner="bussinessPartner"
                :cannotSeeFleet="cannotSeeFleet" :cannotSeeBothRelation="cannotSeeBothRelation"
                :cannotSeeNoBussinessRelation="cannotSeeNoBussinessRelation"
                :canSeeDirectRelation="canSeeDirectRelation" :loading="loading" @store="storeBussinessRelation" />

            <IndirectCustomerNumbers ref="indirectCustomerNumbersRef" />

            <MDBCol col="12" class="text-start" v-if="tenantConfig.showCsNrInfoInProfile">
                <div v-html="$t('account.profile.newCustomerNumbersInfo')" class="pb-3">
                </div>
                <div>
                    {{ $t('account.profile.contactInfo', { email: tenantConfig.contactEmail }) }}
                </div>
            </MDBCol>
        </MDBRow>

        <MDBRow>
            <MDBCol cols="12">
                <MDBBtn type="submit" color="danger" :disabled="loading || disableBtn"
                    class="d-flex justify-content-center align-items-center">
                    <span>{{ $t("global.buttons.send") }}</span>
                    <Loader :show="loading" :small="true" spinner="spinner.svg" :tiny="true" />
                </MDBBtn>
            </MDBCol>
        </MDBRow>
    </VeeForm>
</template>

<script setup>
import VehicleTypeSelector from '@/components/User/ProfileForms/VehicleTypeSelector.vue';
import BusinessPartnerSelector from '@/components/User/ProfileForms/BusinessPartnerSelector.vue';
import IndirectCustomerNumbers from '@/components/User/ProfileForms/IndirectCustomerNumbers.vue';
import { Form as VeeForm } from 'vee-validate';
import { MDBRow, MDBCol, MDBBtn } from 'mdb-vue-ui-kit';
import Loader from '@/components/General/Loader/index.vue';
import { useStore } from 'vuex';
import { ref, computed, watch, onMounted, defineExpose, defineEmits } from 'vue';
import { applyParentAttributesToPreferenceForm } from '@/composables/useProfileParentAttributes';
import { normalizeWholesalersDealersList } from '@/utils/normalizeWholesalersDealers';
import { fetchProfileFormData } from '@/composables/profile';
import { GroupService } from '@/services/group.service';
import { UserService } from '@/services/user.service';
import { toast } from 'vue3-toastify';
import { showErrorToast } from '@/utils/toast';
import { useI18n } from 'vue-i18n';
import { useTenantConfig } from '@/composables/useTenantConfig';
import {
    useAllowDuplicatedCustomerNumber,
    userCanAddUpdateCustomerNumbers
} from '@/composables/useProfileFieldCheck';

const emit = defineEmits(['requestSave', 'errorMessage', 'customerNumberExists']);

const { t } = useI18n();
const { tenantConfig } = useTenantConfig();
const { allowDuplicatedCustomerNumber } = useAllowDuplicatedCustomerNumber(tenantConfig);
const { canAddUpdateCustomerNumbers } = userCanAddUpdateCustomerNumbers(tenantConfig);

import 'vue3-toastify/dist/index.css';

const store = useStore();
const me = computed(() => store.getters.getMe);
const isLite = computed(() => store.getters.isLiteVersion);
const parentAttributes = computed(() => store.getters.getParentAttributes);
const loading = computed(() => store.getters.getPending);

const showTyreAndPartnerFields = computed(() => {
    const attrs = parentAttributes.value;
    return Boolean(attrs?.map && !Array.isArray(attrs));
});

const storedLocale = localStorage.getItem('currentLocale');
const currentCountryCode = computed(() => storedLocale?.split('-')?.[1] ?? '');

const isRelationOptionDisabled = (disabledCodes) => {
    return disabledCodes.includes(currentCountryCode.value);
};

const canSeeDirectRelation = computed(() => {
    return !isRelationOptionDisabled(store.getters.directRelationOptionDisabled);
});

const cannotSeeBothRelation = computed(() => {
    return !isRelationOptionDisabled(store.getters.bothRelationOptionDisabled);
});
const cannotSeeFleet = computed(() => {
    return !isRelationOptionDisabled(store.getters.fleetRelationOptionDisabled);
});
const cannotSeeNoBussinessRelation = computed(() => {
    return !isRelationOptionDisabled(store.getters.noBussinesRelationOptionDisabled);
});

const customerNumbers = ref([{
    customer_number: '',
    wholesaler_id: null,
    expected_name: 'Bridgestone'
}]);
const bussinessPartner = ref(null);
const errorMessage = ref(false);
const preferencesErrorText = ref(null);
const disableBtn = ref(false);
const customer_number = ref(null);
const selections = ref({
    carTyres: false,
    motorcyclesTyres: false,
    truckBusTyres: false,
    agricultureTyres: false,
    otrTyres: false
});
const options = ref([]);

const indirectCustomerNumbersRef = ref(null);

const selectValue = (key) => {
    disableBtn.value = false;
    selections.value[key] = !selections.value[key];

    const valueExists = options.value.includes(key);
    if (valueExists) {
        options.value.splice(options.value.indexOf(key), 1);
    } else {
        options.value.push(key);
    }

    store.dispatch('addInitialParentAttributes', {
        user_id: me.value.id,
        options: options.value,
        selection: [bussinessPartner.value]
    });
};

const syncPreferenceFormFromStore = () => {
    applyParentAttributesToPreferenceForm(parentAttributes.value, {
        selections,
        options,
        bussinessPartner,
    });
};

watch(parentAttributes, syncPreferenceFormFromStore, { immediate: true, deep: true });

onMounted(() => {
    syncPreferenceFormFromStore();
});

watch(
    () => normalizeWholesalersDealersList(me.value?.wholesalers_dealers),
    (dealers) => {
        if (dealers.length > 0) {
            customerNumbers.value = dealers;
        }
    },
    { immediate: true },
);

const storeBussinessRelation = (entry) => {
    bussinessPartner.value = entry;

    store.dispatch('addInitialParentAttributes', {
        user_id: me.value.id,
        options: options.value,
        selection: [bussinessPartner.value]
    });
};

const refreshDealersInStore = async () => {
    if (!store.getters.getIsMePending) {
        await store.dispatch("me");
    }
    await fetchProfileFormData(store);
};

const canEditCustomerNumberEntry = (entry) => {
    return !(entry?.verifiedCustomerNumber && !!entry?.customer_number);
};

const getExistingCustomerNumberRowKey = (entry, index) => entry.id ?? `index-${index}`;

const setCustomerNumberExistsError = (entry, index, isNew = false) => {
    const numbersComponent = indirectCustomerNumbersRef.value;
    if (!numbersComponent) {
        return;
    }

    if (isNew) {
        numbersComponent.setNewCustomerNumberExistsError?.(index);
        return;
    }

    numbersComponent.setExistingCustomerNumberExistsError?.(
        getExistingCustomerNumberRowKey(entry, index),
    );
};

const getCustomerNumberLengthErrorMessage = (customerNumber) =>
    `${t("toast.cannotAddCsNumber")} ${customerNumber} (5-10)`;

const setCustomerNumberLengthError = (entry, index, isNew = false) => {
    const message = getCustomerNumberLengthErrorMessage(entry.customer_number);
    const numbersComponent = indirectCustomerNumbersRef.value;
    if (!numbersComponent) {
        return;
    }

    if (isNew) {
        numbersComponent.setNewCustomerNumberLengthError?.(index, message);
        return;
    }

    numbersComponent.setExistingCustomerNumberLengthError?.(
        getExistingCustomerNumberRowKey(entry, index),
        message,
    );
};

const setPreferencesError = (message) => {
    errorMessage.value = true;
    preferencesErrorText.value = message;
    emit('errorMessage', message);
};

const persistPreferences = async ({ refresh = true, showToast = true } = {}) => {
    errorMessage.value = false;
    preferencesErrorText.value = null;
    indirectCustomerNumbersRef.value?.clearCustomerNumberExistsErrors?.();
    const canManageCustomerNumbers = canAddUpdateCustomerNumbers();
    let savedCustomerNumber = false;

    if (canManageCustomerNumbers && customer_number.value) {
        const csNrData = {
            expected_name: 'Bridgestone',
            customer_number: customer_number.value,
            dealer_id: me.value.plainId,
            id: null,
            can_be_duplicated: allowDuplicatedCustomerNumber()
        };

        const responseCsNr = await UserService.saveCustomerNumber(csNrData);

        if (responseCsNr?.data.message === "exists") {
            setPreferencesError(t("toast.customerNumberExist"));
            showErrorToast(t("toast.customerNumberExist"));
            emit('customerNumberExists');
            return false;
        } else if (responseCsNr?.data.message === "changeParentId") {
            setPreferencesError(t("toast.changeParentId"));
            showErrorToast(t("toast.changeParentId"));
            return false;
        } else if (responseCsNr?.status === 200 || responseCsNr?.status === 201) {
            customer_number.value = null;
            savedCustomerNumber = true;
        } else {
            setPreferencesError(t("toast.apiError"));
            showErrorToast(t("toast.apiError"));
            return false;
        }
    }

    if (canManageCustomerNumbers && customerNumbers.value.length > 0) {
        for (let index = 0; index < customerNumbers.value.length; index++) {
            const element = customerNumbers.value[index];
            if (!canEditCustomerNumberEntry(element)) {
                continue;
            }

            if (element.customer_number.length > 0) {
                const lengthCheck = element.customer_number.length < 5 || element.customer_number.length > 10;

                if (lengthCheck) {
                    const message = getCustomerNumberLengthErrorMessage(element.customer_number);
                    setCustomerNumberLengthError(element, index);
                    setPreferencesError(message);
                    showErrorToast(message);
                    emit('customerNumberExists');
                    continue;
                }

                const csNrData = {
                    expected_name: 'Bridgestone',
                    customer_number: element.customer_number,
                    wholesaler_id: element.wholesaler_id,
                    dealer_id: me.value.plainId,
                    id: element.id,
                    can_be_duplicated: allowDuplicatedCustomerNumber()
                };

                const respCsNr = await UserService.saveCustomerNumber(csNrData);

                if (respCsNr?.data.message === "exists") {
                    setCustomerNumberExistsError(element, index);
                    setPreferencesError(t("toast.customerNumberExist") + ' ' + element.customer_number);
                    showErrorToast(t("toast.customerNumberExist") + ' ' + element.customer_number);
                    emit('customerNumberExists');
                    continue;
                } else if (respCsNr?.data.message === "changeParentId") {
                    setPreferencesError(t("toast.changeParentId"));
                    showErrorToast(t("toast.changeParentId"));
                    continue;
                } else if (respCsNr?.status === 200 || respCsNr?.status === 201) {
                    savedCustomerNumber = true;
                } else {
                    setPreferencesError(t("toast.apiError"));
                    showErrorToast(t("toast.apiError"));
                }
            }
        }
    }

    if (canManageCustomerNumbers) {

        const numbersComponent = indirectCustomerNumbersRef.value;
        const indirectCustomerNumbers = numbersComponent?.getNewCustomerNumbers?.() ?? [];

        for (let index = 0; index < indirectCustomerNumbers.length; index++) {
            const element = indirectCustomerNumbers[index];
            if (element.customer_number && element.customer_number.length >= 5 && element.customer_number.length <= 10) {
                const csNrData = {
                    expected_name: element.expected_name || null,
                    customer_number: element.customer_number,
                    wholesaler_id: element.wholesaler_id,
                    dealer_id: me.value.plainId,
                    id: null,
                    can_be_duplicated: allowDuplicatedCustomerNumber()
                };

                try {
                    const respCsNr = await UserService.saveCustomerNumber(csNrData);
                    if (respCsNr?.data.message === "exists") {
                        setCustomerNumberExistsError(element, index, true);
                        setPreferencesError(t("toast.customerNumberExist") + ' ' + element.customer_number);
                        showErrorToast(t("toast.customerNumberExist") + ' ' + element.customer_number);
                        emit('customerNumberExists');
                        continue;
                    } else if (respCsNr?.data.message === "changeParentId") {
                        setPreferencesError(t("toast.changeParentId"));
                        showErrorToast(t("toast.changeParentId"));
                        continue;
                    } else if (respCsNr?.status === 200 || respCsNr?.status === 201) {
                        savedCustomerNumber = true;
                    } else {
                        setPreferencesError(t("toast.apiError"));
                        showErrorToast(t("toast.apiError"));
                    }

                } catch (err) {
                    setPreferencesError(t("toast.apiError"));
                    showErrorToast(t("toast.apiError"));
                }
            } else if (element.customer_number && element.customer_number.length > 0) {
                const message = getCustomerNumberLengthErrorMessage(element.customer_number);
                setCustomerNumberLengthError(element, index, true);
                setPreferencesError(message);
                showErrorToast(message);
                emit('customerNumberExists');
            }
        }
    }

    if (errorMessage.value) {
        emit('errorMessage', preferencesErrorText.value);
        return false;
    }

    if (isLite.value) {
        const normalizedSelection = bussinessPartner.value || 'noBusinessRelationship';
        const input = {
            user_id: me.value.plainId,
            options: options.value,
            selection: [normalizedSelection]
        };

        const response = await GroupService.parentAttributeAsignSelector(input);

        if (response && response.status === 200) {
            if (showToast) {
                toast(t("toast.profileSaved"), { type: "success", theme: "colored", transition: toast.TRANSITIONS.SLIDE, position: toast.POSITION.BOTTOM_RIGHT });
            }
            if (refresh) {
                await refreshDealersInStore();
            }
        } else {
            const selectionErrorMessage =
                response?.response?.data?.errors?.['selection.0'] ||
                response?.response?.data?.message ||
                t("toast.apiError");
            showErrorToast(selectionErrorMessage);

            setPreferencesError(selectionErrorMessage);
            return false;
        }
    }

    if (savedCustomerNumber) {
        indirectCustomerNumbersRef.value?.clearNewCustomerNumbers?.();
        indirectCustomerNumbersRef.value?.clearCustomerNumberExistsErrors?.();
        if (refresh) {
            await refreshDealersInStore();
        }
        if (showToast && !isLite.value) {
            toast(t("toast.customerNumberSaved"), {
                type: "success",
                theme: "colored",
                transition: toast.TRANSITIONS.SLIDE,
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        }
    }

    emit('errorMessage', null);

    return true;
};

const onSubmit = () => {
    emit('requestSave');
};

defineExpose({
    persistPreferences,
});
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/profile.scss";
</style>
